import { useAppSelector } from "../../../redux/hooks";
import { useSiteContext } from "../../SiteProvider";
import { buildFooterConfig } from "./BrandStore/config";
import { useMemo } from "react";
import { removeTrailingSlash, StoreBrandingType } from "@brandclub/common-ui";
import { SiteBrandingType } from "../../../types/misc";
import { useSignedIn } from "@/utils/hooks/useSignedIn";
import useStoreType from "@/utils/hooks/useStoreType";

export interface RewardSummaryForBrand {
  lifetimeEarnings: number;
  lifetimePayouts: number;
  lifetimeAdjustments: number;
  lockedBalance: number;
  pendingBalance: number;
  unlockedBalance: number;
  cancelledBalance: number;
  hasLoaded: boolean;
}

interface UseFooterConfigsProps {
  rewardSummaryForBrand: RewardSummaryForBrand;
  totalRewardCounts: number;
  currentBalance: number | undefined;
}

export const useFooterConfigs = ({
  rewardSummaryForBrand,
  totalRewardCounts,
  currentBalance,
}: UseFooterConfigsProps) => {
  const mobileAppLink = useAppSelector(
    ({ appConfig }) => appConfig?.domainConfig.mobileAppLink
  );
  const storeDescription = useAppSelector(
    ({ appConfig }) => appConfig?.domainConfig.storeDescription
  );
  const storeType = useStoreType();
  const brandEntity = useAppSelector(({ brandEntity }) => brandEntity);
  const brandAppConfig = useAppSelector(
    ({ appConfig }) => appConfig?.brandAppConfig
  );
  const isDtc = storeType === StoreBrandingType.CustomDTCStore;
  const enableRewards = isDtc
    ? brandAppConfig?.modulesConfig?.rewardsModule?.enabled
    : true;
  const enableMembership = isDtc
    ? brandAppConfig?.modulesConfig?.membershipModule?.enabled
    : true;

  const { signedIn } = useSignedIn();
  const { siteBrandingType, site, persistentQueryParams, routePrefix } =
    useSiteContext();

  const userRewardAmount =
    rewardSummaryForBrand.lifetimeEarnings -
    rewardSummaryForBrand.lifetimePayouts -
    rewardSummaryForBrand.lifetimeAdjustments;

  const appLink = mobileAppLink
    ? removeTrailingSlash(mobileAppLink) + "/getapp"
    : "";

  return useMemo(() => {
    const config = buildFooterConfig({
      siteBrandingType,
      userRewardAmount,
      userRewardCounts: totalRewardCounts,
      site,
      routePrefix,
      persistentQueryParams,
      signedIn,
      brandEntity,
      appLink,
      customText: storeDescription,
      enableRewards,
      enableMembership,
    });

    const universalStoreFooterConfig = buildFooterConfig({
      siteBrandingType: SiteBrandingType.UniversalStore,
      userRewardAmount: currentBalance,
      userRewardCounts: totalRewardCounts,
      site: undefined,
      routePrefix: "",
      persistentQueryParams,
      signedIn,
      appLink,
    });

    if (siteBrandingType === SiteBrandingType.UniversalStore) {
      return [universalStoreFooterConfig];
    }

    if (
      siteBrandingType === SiteBrandingType.CustomStore ||
      siteBrandingType === SiteBrandingType.CustomDTCStore
    ) {
      return [config];
    }

    return [config, universalStoreFooterConfig];
  }, [
    routePrefix,
    siteBrandingType,
    currentBalance,
    totalRewardCounts,
    site,
    persistentQueryParams,
    userRewardAmount,
    brandEntity,
    signedIn,
    appLink,
    storeDescription,
    enableRewards,
  ]);
};
